import React, { FC, useMemo } from "react";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import { numberWithCommas } from "../../utils/helpers";
import {
  GridPageSizeControl,
  GridPageSizeSelect,
  GridPaginationButton,
  GridPaginationControls,
  GridPaginationIconButton,
  GridPaginationPageLabel,
  GridPaginationPanel,
  GridPaginationSummary,
} from "./Grid.styles";

const GRID_PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250];

export const DEFAULT_GRID_PAGE_SIZE = 100;

export const GridPagination: FC<{
  currentPage: number;
  pageCount: number;
  pageEndIndex: number;
  pageSize: number;
  pageStartIndex: number;
  rowCount: number;
  totalReqs?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}> = ({
  currentPage,
  pageCount,
  pageEndIndex,
  pageSize,
  pageStartIndex,
  rowCount,
  totalReqs,
  onPageChange,
  onPageSizeChange,
}) => {
  const lastPage = pageCount - 1;
  const paginationSummary = useMemo(() => {
    const totalSummary = totalReqs
      ? ` (Total: ${numberWithCommas(totalReqs)})`
      : "";
    return `${numberWithCommas(pageStartIndex + 1)} to ${numberWithCommas(
      pageEndIndex,
    )} of ${numberWithCommas(rowCount)}${totalSummary}`;
  }, [pageEndIndex, pageStartIndex, rowCount, totalReqs]);

  if (!rowCount) return null;

  return (
    <GridPaginationPanel>
      <GridPageSizeControl>
        <span>Page Size:</span>
        <GridPageSizeSelect
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
        >
          {GRID_PAGE_SIZE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </GridPageSizeSelect>
      </GridPageSizeControl>
      <GridPaginationSummary>{paginationSummary}</GridPaginationSummary>
      <GridPaginationControls>
        <GridPaginationIconButton
          aria-label="First page"
          disabled={currentPage === 0}
          onClick={() => onPageChange(0)}
        >
          <FirstPageIcon fontSize="small" />
        </GridPaginationIconButton>
        <GridPaginationButton
          disabled={currentPage === 0}
          onClick={() => onPageChange(Math.max(0, currentPage - 1))}
        >
          PREV
        </GridPaginationButton>
        <GridPaginationPageLabel>
          Page {numberWithCommas(currentPage + 1)} of{" "}
          {numberWithCommas(pageCount)}
        </GridPaginationPageLabel>
        <GridPaginationButton
          disabled={currentPage >= lastPage}
          onClick={() => onPageChange(Math.min(lastPage, currentPage + 1))}
        >
          NEXT
        </GridPaginationButton>
        <GridPaginationIconButton
          aria-label="Last page"
          disabled={currentPage >= lastPage}
          onClick={() => onPageChange(lastPage)}
        >
          <LastPageIcon fontSize="small" />
        </GridPaginationIconButton>
      </GridPaginationControls>
    </GridPaginationPanel>
  );
};
